import {
  Component,
  ElementRef,
  HostListener,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  CompanyUsersService,
  type CompanyUserRef,
} from '../../../core/services/company-users.service';
import { I18nService } from '../../../core/i18n';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';

/** Membro da empresa apresentado no painel de sugestões. */
export type MemberEmailSuggestion = CompanyUserRef;

/**
 * Campo de e-mail com sugestão dos membros já cadastrados na empresa.
 *
 * Continua sendo um input livre de propósito: quem digita um e-mail que ainda não existe na
 * empresa segue no fluxo de convite automático das telas de membros. As sugestões só evitam
 * ter de lembrar o endereço completo de quem já está cadastrado.
 */
@Component({
  selector: 'app-member-email-autocomplete',
  standalone: true,
  imports: [UserAvatarComponent],
  template: `
    <div class="mea-root">
      <input
        type="email"
        class="mea-input"
        role="combobox"
        aria-autocomplete="list"
        [attr.aria-expanded]="panelOpen()"
        [attr.aria-controls]="panelOpen() ? listId : null"
        [attr.aria-activedescendant]="activeOptionId()"
        autocomplete="off"
        spellcheck="false"
        [disabled]="disabled()"
        [value]="value()"
        [placeholder]="placeholder()"
        (input)="onInput($event)"
        (focus)="onFocus()"
        (keydown)="onKeydown($event)"
      />

      @if (panelOpen()) {
        <ul
          class="mea-panel"
          [id]="listId"
          role="listbox"
          [style.top.px]="panelGeom()?.top"
          [style.left.px]="panelGeom()?.left"
          [style.width.px]="panelGeom()?.width"
        >
          @for (item of suggestions(); track item.id; let i = $index) {
            <li
              class="mea-option"
              role="option"
              [id]="listId + '-' + i"
              [class.is-active]="i === activeIndex()"
              [attr.aria-selected]="i === activeIndex()"
              (mousedown)="onOptionMouseDown($event, item)"
              (mouseenter)="activeIndex.set(i)"
            >
              <app-user-avatar
                [userId]="item.id"
                [label]="item.name"
                [photoOverride]="item.avatarUrl"
                [size]="26"
              ></app-user-avatar>
              <span class="mea-option__text">
                <span class="mea-option__name">{{ item.name }}</span>
                <span class="mea-option__email">{{ item.email }}</span>
              </span>
            </li>
          }

          @if (!suggestions().length) {
            <li class="mea-empty" role="presentation">
              @if (usersLoading()) {
                {{ t('memberEmailAutocomplete.loading', undefined, 'buscando membros…') }}
              } @else {
                {{
                  t(
                    'memberEmailAutocomplete.empty',
                    undefined,
                    'nenhum membro encontrado; o e-mail digitado receberá convite.'
                  )
                }}
              }
            </li>
          }
        </ul>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        flex: 1;
        min-width: 200px;
      }

      .mea-root {
        position: relative;
        width: 100%;
      }

      /* Espelha .field-input das telas de membros para o campo não destoar da linha. */
      .mea-input {
        width: 100%;
        height: 36px;
        border: 1px solid var(--border);
        border-radius: var(--r-sm, 8px);
        padding: 0 10px;
        font-size: 13px;
        background: var(--bg-surface);
        color: var(--text);
        outline: none;
      }

      .mea-input:focus {
        border-color: color-mix(in srgb, var(--border) 40%, var(--text));
      }

      .mea-input:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      /* Posicionado em viewport: dentro dos modais o corpo rola com overflow e cortaria o painel. */
      .mea-panel {
        position: fixed;
        z-index: 1200;
        margin: 0;
        padding: 4px;
        list-style: none;
        max-height: 264px;
        overflow-y: auto;
        background: var(--bg-surface);
        border: 1px solid var(--border);
        border-radius: var(--r-md, 12px);
        box-shadow: 0 10px 24px rgb(0 0 0 / 12%);
      }

      .mea-option {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 6px 8px;
        border-radius: 8px;
        cursor: pointer;
      }

      .mea-option.is-active {
        background: color-mix(in srgb, var(--text) 7%, transparent);
      }

      .mea-option__text {
        display: grid;
        min-width: 0;
      }

      .mea-option__name {
        font-size: 13px;
        color: var(--text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .mea-option__email {
        font-size: 11px;
        color: var(--muted);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .mea-empty {
        padding: 8px;
        font-size: 12px;
        line-height: 1.4;
        color: var(--muted);
      }
    `,
  ],
})
export class MemberEmailAutocompleteComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly companyUsers = inject(CompanyUsersService);
  private readonly i18n = inject(I18nService);
  readonly t = this.i18n.t.bind(this.i18n);

  readonly value = input<string>('');
  readonly placeholder = input<string>('');
  readonly disabled = input<boolean>(false);
  /** E-mails já na lista da tela: saem das sugestões para não oferecer quem já está lá. */
  readonly excludeEmails = input<string[]>([]);
  /** Caracteres mínimos antes de sugerir; abaixo disso o painel nem abre. */
  readonly minChars = input<number>(3);
  readonly maxSuggestions = input<number>(8);

  readonly valueChange = output<string>();
  /** Enter sem sugestão destacada: a tela decide o que fazer (adicionar/convidar). */
  readonly submitted = output<void>();

  readonly listId = `mea-list-${Math.random().toString(36).slice(2, 9)}`;

  private readonly destroyRef = inject(DestroyRef);
  private readonly scrollCleanups: Array<() => void> = [];
  readonly panelGeom = signal<{ top: number; left: number; width: number } | null>(null);
  private readonly dismissed = signal(false);
  readonly activeIndex = signal(-1);
  readonly usersLoading = this.companyUsers.loading;

  private readonly excludedSet = computed(
    () =>
      new Set(
        this.excludeEmails()
          .map((e) => e.trim().toLowerCase())
          .filter(Boolean)
      )
  );

  readonly suggestions = computed<MemberEmailSuggestion[]>(() => {
    const term = this.normalize(this.value());
    if (term.length < this.minChars()) return [];
    const excluded = this.excludedSet();

    return this.companyUsers
      .users()
      .filter((u) => {
        const email = (u.email ?? '').trim();
        if (!email || excluded.has(email.toLowerCase())) return false;
        return this.normalize(u.name).includes(term) || this.normalize(email).includes(term);
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      .slice(0, this.maxSuggestions());
  });

  /** Painel abre com termo suficiente; some quando o utilizador fecha com Esc ou já escolheu. */
  readonly panelOpen = computed(
    () =>
      !this.disabled() &&
      !this.dismissed() &&
      this.normalize(this.value()).length >= this.minChars()
  );

  readonly activeOptionId = computed(() => {
    const i = this.activeIndex();
    return this.panelOpen() && i >= 0 && i < this.suggestions().length
      ? `${this.listId}-${i}`
      : null;
  });

  constructor() {
    void this.companyUsers.ensureLoaded();

    effect((onCleanup) => {
      if (!this.panelOpen()) {
        this.panelGeom.set(null);
        this.unbindScrollSync();
        return;
      }
      queueMicrotask(() => {
        if (!this.panelOpen()) return;
        this.syncGeom();
        this.bindScrollSync();
      });
      onCleanup(() => this.unbindScrollSync());
    });

    this.destroyRef.onDestroy(() => this.unbindScrollSync());
  }

  /** Ancora o painel ao input em coordenadas de viewport. */
  private syncGeom(): void {
    const field = this.host.nativeElement.querySelector('.mea-input');
    if (!(field instanceof HTMLElement)) return;
    const r = field.getBoundingClientRect();
    const margin = 8;
    const vw = typeof window !== 'undefined' ? window.innerWidth : r.width;
    const width = Math.min(Math.round(r.width), Math.max(margin * 2, vw - margin * 2));
    let left = Math.round(r.left);
    if (left + width > vw - margin) left = Math.max(margin, vw - margin - width);
    if (left < margin) left = margin;
    this.panelGeom.set({ top: Math.round(r.bottom + 4), left, width });
  }

  private bindScrollSync(): void {
    this.unbindScrollSync();
    const sync = () => this.syncGeom();
    window.addEventListener('resize', sync);
    this.scrollCleanups.push(() => window.removeEventListener('resize', sync));

    // Segue qualquer ancestral rolável (corpo do modal, painel da página).
    let el: HTMLElement | null = this.host.nativeElement;
    while (el) {
      const st = getComputedStyle(el);
      if (/(auto|scroll|overlay)/.test(st.overflowY) || /(auto|scroll|overlay)/.test(st.overflowX)) {
        const target = el;
        target.addEventListener('scroll', sync, { passive: true });
        this.scrollCleanups.push(() => target.removeEventListener('scroll', sync));
      }
      el = el.parentElement;
    }
  }

  private unbindScrollSync(): void {
    while (this.scrollCleanups.length) this.scrollCleanups.pop()?.();
  }

  onFocus(): void {
    void this.companyUsers.ensureLoaded();
    this.dismissed.set(false);
  }

  onInput(event: Event): void {
    const text = (event.target as HTMLInputElement | null)?.value ?? '';
    this.dismissed.set(false);
    this.activeIndex.set(-1);
    this.valueChange.emit(text);
  }

  /** `mousedown` e não `click`: o blur do input dispararia antes e fecharia o painel. */
  onOptionMouseDown(event: MouseEvent, item: MemberEmailSuggestion): void {
    event.preventDefault();
    this.select(item);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.panelOpen()) {
        event.stopPropagation();
        this.dismissed.set(true);
        this.activeIndex.set(-1);
      }
      return;
    }

    const open = this.panelOpen();
    const items = this.suggestions();

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!open || !items.length) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = this.activeIndex() + step;
      this.activeIndex.set(next < 0 ? items.length - 1 : next >= items.length ? 0 : next);
      return;
    }

    if (event.key === 'Enter') {
      const i = this.activeIndex();
      if (open && i >= 0 && i < items.length) {
        event.preventDefault();
        this.select(items[i]);
        return;
      }
      this.dismissed.set(true);
      this.submitted.emit();
    }
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: PointerEvent): void {
    if (!this.panelOpen()) return;
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.dismissed.set(true);
    }
  }

  private select(item: MemberEmailSuggestion): void {
    this.dismissed.set(true);
    this.activeIndex.set(-1);
    this.valueChange.emit(item.email);
  }

  /** Busca tolerante a acento e caixa: "jose" encontra "José". */
  private normalize(value: string | null | undefined): string {
    return (value ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
}
